import useGoogleTracking from '@hooks/useGoogleTracking'
import useTagmanager from '@hooks/useTagmanager'
import { getStoryblokApi } from '@storyblok/react'
import {
  GET_STUDIO_BY_SLUG,
  GET_STUDIOS,
} from 'api/symfony/graphql/queries/getStudios'
import { parseStudiosResponse } from 'api/symfony/parsers/graphqlParsers'
import Head from 'next/head'
import { useRouter } from 'next/router'
import Script from 'next/script'
import type {
  GetStaticPaths,
  GetStaticProps,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from 'next/types'
import { transformToNested } from 'pages/sunpoint'
import React, { useEffect } from 'react'
import { ISbLinks } from 'storyblok-js-client'

import Headline from '@components/atoms/Headline'
import { HeadlineImportances } from '@components/atoms/Headline/Headline'
import MetaTags from '@components/molecules/MetaTags'
import { BackupMetaWellmaxx } from '@components/molecules/MetaTags/MetaTags'
import StudioDetailPage from '@components/pages/StudioDetailPage'
import WellmaxxTemplateDefault from '@components/templates/WellmaxxTemplateDefault'

import RealEnvironmentContext from '@helper/context/RealEnviromentContext'
import { useUserData } from '@helper/context/UserDataContext'
import { getStoryblokVersion } from '@helper/utils/getStoryblokVersion'

import { ISR_REVALIDATE_SECONDS } from '@definitions/config/isr'
import { RoutesWellmaxx } from '@definitions/routes/RoutesWellmaxx'
import { Studio } from '@definitions/types/symfonyTypes'

import { query } from '@server/symfony/apollo-query'

interface StudioIndexProps {
  menuData: ISbLinks
  studio: Studio | null
  isDraftMode: boolean
}

export default function StudioIndex({
  menuData,
  studio,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { userData, setUserData } = useUserData()
  const { trackPageView } = useGoogleTracking()
  const router = useRouter()
  useTagmanager()

  useEffect(() => {
    trackPageView(router.asPath, studio?.title ?? 'Studios - Wellmaxx')
  }, [router.asPath, studio?.title, trackPageView])

  return (
    <WellmaxxTemplateDefault menuData={transformToNested(menuData)}>
      <Head>
        <title>{'Studios - WELLMAXX bodyforming'}</title>
        <meta
          name="description"
          content="Innovatives & effektives Körper-Abnehm-Programm | WELLMAXX bodyforming"
        />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/favicons/wellmaxx/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicons/wellmaxx/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicons/wellmaxx/favicon-16x16.png"
        />
        <link rel="manifest" href="/favicons/wellmaxx/site.webmanifest" />
        <link
          rel="mask-icon"
          href="/favicons/wellmaxx/safari-pinned-tab.svg"
          color="#981858"
        />
        <link rel="shortcut icon" href="/favicons/wellmaxx/favicon.ico" />
        <meta name="msapplication-TileColor" content="#981858" />
        <meta
          name="msapplication-config"
          content="/favicons/wellmaxx/browserconfig.xml"
        />
        <meta name="theme-color" content="#981858" />
        <link
          rel="canonical"
          href={
            process.env.NEXT_PUBLIC_APP_URL
              ? `${process.env.NEXT_PUBLIC_APP_URL}${router.asPath || ''}`
              : ''
          }
          suppressHydrationWarning
        />
      </Head>
      {studio !== null && (
        <MetaTags
          _uid={studio?.id}
          title={studio?.title + ' | WELLMAXX bodyforming'}
          description={studio?.seoDescription}
          og_description={studio?.seoDescription}
          og_title={studio?.title + ' | WELLMAXX bodyforming'}
          og_image={'/img/og_1200_630.jpg'}
          twitter_image={'/img/og_1200_630.jpg'}
          twitter_title={studio?.title + ' | WELLMAXX bodyforming'}
          twitter_description={studio?.seoDescription}
        />
      )}
      {studio === null && <BackupMetaWellmaxx />}
      <Script
        data-settings-id={process.env.NEXT_PUBLIC_USER_CENTRICS_ID ?? ''}
        id="usercentrics-cmp"
        src="https://app.usercentrics.eu/browser-ui/latest/loader.js"
      ></Script>
      <RealEnvironmentContext.Provider value={{ isRealEnvironment: true }}>
        <div>
          {studio === null && (
            <Headline importance={HeadlineImportances.h1}>
              Kein Studio gefunden
            </Headline>
          )}
          {studio && (
            <StudioDetailPage
              studio={studio}
              handleSelectContract={(contract) => {
                setUserData({
                  studio: studio,
                  contract: contract,
                  formData: userData.formData,
                })
                router.push(RoutesWellmaxx.WELLMAXX__CLUBMITGLIEDWERDEN)
              }}
            />
          )}
        </div>
      </RealEnvironmentContext.Provider>
    </WellmaxxTemplateDefault>
  )
}

export const getStaticProps: GetStaticProps<StudioIndexProps> = async (
  context: GetStaticPropsContext
) => {
  const slug = context.params?.slug
  if (!slug || !Array.isArray(slug)) {
    return {
      notFound: true,
    }
  }

  const storyblokApi = getStoryblokApi()
  const storyblokVersion = getStoryblokVersion(context.draftMode)
  let studio: Studio | undefined = undefined
  let links

  try {
    let { data: linkData } = await storyblokApi.get('cdn/links', {
      version: storyblokVersion,
      per_page: 5000,
      starts_with: 'wellmaxx',
      is_startpage: true,
      with_tag: 'InHeaderNavigation',
    })

    if (linkData) {
      links = linkData.links
    }
  } catch (error) {
    console.error('Fehler beim Abrufen der Links:', error)
  }

  try {
    const slugString = slug.join('/')
    const { data } = await query({
      query: GET_STUDIO_BY_SLUG,
      variables: {
        slug: slugString,
      },
      fetchPolicy: 'no-cache',
    })
    const studios = parseStudiosResponse(data?.studios ?? null)
    studio = studios[0]
  } catch (error) {
    console.error('Fehler beim Abrufen der Studios:', error)
  }

  return {
    props: {
      menuData: links ?? {},
      studio: studio ?? null,
      isDraftMode: context.draftMode ?? false,
    },
    revalidate: ISR_REVALIDATE_SECONDS,
  }
}

export const getStaticPaths: GetStaticPaths<{ slug: string[] }> = async () => {
  let studios: Studio[] = []

  try {
    const { data } = await query({
      query: GET_STUDIOS,
    })

    studios = parseStudiosResponse(data?.studios ?? null)
  } catch (error) {
    console.error('Fehler beim Abrufen der Studios:', error)
  }

  const paths = studios.map((studio: Studio) => {
    const splittedSlug = studio.slug
      .split('/')
      .filter((el: string) => el.length != 0)
    return {
      params: { slug: splittedSlug, locale: 'de' },
    }
  })

  return {
    paths,
    fallback: 'blocking',
  }
}
